import { IPRAttributes } from '../ipr';
import { PiuProductState } from './types';

/**
 * The parts of `PiuProductState` needed to tell a leaf product from a container row.
 */
type PiuHierarchyNode = Pick<PiuProductState, 'id' | 'parentId' | 'isSegment'>;

/**
 * The leaf products of a flat product-input set: everything that is neither a segment /
 * business-unit container (`isSegment`) nor a grandfathered product-parent (a product that
 * some other row points at via `parentId`).
 *
 * Container rows exist only to group; they carry no taxonomy mapping and never reach results,
 * so anything user-facing that talks about "products" must count leaves, not raw rows.
 */
export function pickPiuLeafProducts<T extends PiuHierarchyNode>(products: T[]): T[] {
  const parentIds = new Set(products.map(p => p.parentId).filter((id): id is string => id != null));
  return products.filter(p => p.isSegment !== true && !parentIds.has(p.id));
}

/**
 * Number of leaf products in a flat product-input set — see `pickPiuLeafProducts`.
 */
export function countPiuLeafProducts(products: PiuHierarchyNode[] | undefined): number {
  return products === undefined ? 0 : pickPiuLeafProducts(products).length;
}

interface MergedIprParams {
  ipr: Partial<IPRAttributes> | undefined;
  manualIprEdits: Partial<IPRAttributes> | undefined;
}

export function getMergedIpr(product: MergedIprParams): Partial<IPRAttributes> {
  const ipr = product.ipr;
  if (!ipr) {
    return product.manualIprEdits || {};
  }
  const manualIprEdits = product.manualIprEdits;
  const mergedIpr = { ...ipr, ...manualIprEdits };
  return mergedIpr;
}
